import { useState } from 'react';
import type { AccountTypeOption, ChartAccountRow, ChartOfAccountsOptions } from '@/core/models/trial-balance.models';
import { notifyError } from '@/core/notify/notify';
import { trialBalanceService } from '@/core/services/trialBalance';
import { ACCOUNT_CODE_MAX_LENGTH, accountCodeProblem, accountTypesFor } from '@/core/utils/chartAccount';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { validateForm } from '@/features/manage/formRules';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './AddNewAccount.scss';

export interface AddNewAccountDialogData {
  clientId: string;
  options: ChartOfAccountsOptions;
  account?: ChartAccountRow;
  /** BUG-040: a new account's name and code filled in from what was being looked for (e.g. a trial balance search). */
  prefill?: { accountName?: string; accountCode?: string };
}

/**
 * Angular AddNewAccountComponent: add or edit a chart of accounts account. Closes with the saved account after a save
 * (nothing when cancelled), so a caller can use the account it has just created.
 */
export function AddNewAccount({ data, dialogRef }: DialogComponentProps<AddNewAccountDialogData, ChartAccountRow>) {
  const account = data?.account;
  // BUG-018: Partners Emoluments is for a partnership or an LLP only; an account that already has a type keeps it on offer.
  const clientType = data?.options?.clientType;
  const accountTypes = accountTypesFor(
    data?.options?.accountTypes ?? [],
    clientType === null || clientType === undefined ? [] : [clientType],
    account?.accountTypeId,
  );
  const vatCodes = data?.options?.vatCodes ?? [];

  const [accountTypeId, setAccountTypeId] = useState<number | null>(account?.accountTypeId ?? null);
  const [accountName, setAccountName] = useState(account?.accountName ?? data?.prefill?.accountName ?? '');
  const [accountCode, setAccountCode] = useState(
    account?.code ?? (data?.prefill?.accountCode ?? '').slice(0, ACCOUNT_CODE_MAX_LENGTH),
  );
  const [vatCode, setVatCode] = useState<string | null>(account ? account.vatCode : null);
  const [isActive, setIsActive] = useState(account ? account.isActive : true);
  const [isSaving, setIsSaving] = useState(false);
  /** True after a save was refused: the mandatory fields that are still empty are outlined red. */
  const [submitted, setSubmitted] = useState(false);

  const onSave = () => {
    if (isSaving) {
      return;
    }
    setSubmitted(true);
    // Account type, name and code are required by the API (ChartOfAccountsService).
    if (!validateForm(!!accountTypeId && !!accountName?.trim() && !!accountCode?.trim())) {
      return;
    }
    // BUG-018: letters, numbers and / - . # & ( ) (4610/1), at most 10 characters, as the old application took.
    const codeProblem = accountCodeProblem(accountCode.trim());
    if (codeProblem) {
      notifyError(codeProblem);
      return;
    }
    if (!data?.clientId) {
      notifyError('Select a client from Accounts first.');
      return;
    }

    setIsSaving(true);
    trialBalanceService
      .saveAccount({
        clientId: data.clientId,
        id: account?.id ?? null,
        accountTypeId: accountTypeId!,
        accountName: accountName.trim(),
        accountCode: accountCode.trim(),
        vatCode: vatCode || null,
        isActive: !!isActive,
      })
      .then(
        (saved) => dialogRef.close(saved),
        (error) => {
          setIsSaving(false);
          notifyError(error);
        },
      );
  };

  return (
    <div className="mat-dialog-wrapper new-account">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{account ? 'Edit Account' : 'Add New Account'}</span>
                </h2>
                <DialogClose aria-label="Close">
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field na-row">
              <p className="fw-bold">
                Account Type <RequiredMark />
              </p>
              <div className="na-control">
                <Select<AccountTypeOption>
                  className={`w-select-type${submitted && !accountTypeId ? ' ng-invalid ng-touched' : ''}`}
                  openOnFocus
                  placeholder="Select Account Type"
                  name="accountTypeId"
                  items={accountTypes}
                  bindLabel="name"
                  bindValue="id"
                  groupBy="groupName"
                  value={accountTypeId}
                  onChange={(value) => setAccountTypeId((value as number | null) ?? null)}
                />
              </div>
            </div>
            <div className="form-field na-row">
              <p className="fw-bold">
                Account Name <RequiredMark />
              </p>
              <div className="na-control">
                <InputField
                  fieldClassName="w-input-name"
                  invalid={submitted && !accountName.trim()}
                  charMode="printable"
                  maxLength={255}
                  name="accountName"
                  value={accountName}
                  onChange={(event) => setAccountName(event.target.value)}
                />
              </div>
            </div>
            <div className="form-field na-row">
              <p className="fw-bold">
                Account Code <RequiredMark />
              </p>
              <div className="na-control">
                <InputField
                  fieldClassName="w-input-code"
                  invalid={submitted && !accountCode.trim()}
                  name="accountCode"
                  maxLength={ACCOUNT_CODE_MAX_LENGTH}
                  placeholder="e.g. 1000"
                  value={accountCode}
                  // `maxLength` only limits real typing/paste; a value set programmatically (e.g. in tests) skips it,
                  // so the slice keeps the hard cap ap5-a4 relied on.
                  onChange={(event) => setAccountCode(event.target.value.slice(0, ACCOUNT_CODE_MAX_LENGTH))}
                />
              </div>
            </div>
            <div className="form-field na-row">
              <p className="fw-bold">VAT Code</p>
              <div className="na-control">
                <Select<string>
                  className="w-select-vat"
                  openOnFocus
                  placeholder="No VAT"
                  name="vatCode"
                  items={vatCodes}
                  value={vatCode}
                  onChange={(value) => setVatCode((value as string | null) ?? null)}
                />
              </div>
            </div>
            <div className="na-checkbox-row">
              <div className="na-label-spacer" />
              <div className="na-control">
                <Checkbox name="isActive" checked={isActive} onChange={(event) => setIsActive(event.target.checked)}>
                  Active
                </Checkbox>
              </div>
            </div>
          </div>

          <div className="na-actions-footer">
            <DialogClose as="button" type="button" className="btn-back" aria-label="Cancel" title="Cancel">
              <Icon name="arrow_back" />
            </DialogClose>
            <Button
              className="action-button primary-button btn-save-account"
              type="button"
              busy={isSaving}
              onClick={onSave}
            >
              <Icon name="save" />
              <span>Save</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
